import { datapoints } from '../datapoints';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { FolderSelectorError } from '../store/schema/FolderSelectorError';
import { getBreadcrumbForFolderId } from '../selectors/breadcrumbsSelectors';
import { getCurrentLoadedFolderId } from '../selectors/selectedFolderSelectors';
import { getFolderModelForId } from '../selectors/folderModelSelectors';
import { getFoldersForProvider } from '../services/getFoldersForProvider';
import { getSelectedProvider } from '../selectors/selectedProviderSelectors';
import { orchestrator } from 'satcheljs';
import { PROVIDER_ROOT_FOLDER_BREADCRUMB_ID } from '../constants';
import { providersLoaded } from 'owa-fileprovider-store';
import { trace } from 'owa-trace';
import {
    addBreadcrumb,
    breadcrumbSelected,
    clearAllBreadcrumbs,
    clearAllBreadcrumbsUpTill,
    folderSelected,
    foldersLoaded,
    loadFolders,
    loading,
    loadingFoldersFailed,
    reloadFolders,
    stoppedLoading,
} from '../actions/internalActions';

orchestrator(providersLoaded, () => {
    loadFolders();
});

orchestrator(folderSelected, actionMessage => {
    const { folderId } = actionMessage;
    loadFolders(folderId);
});

orchestrator(reloadFolders, () => {
    const folderId = getCurrentLoadedFolderId();
    loadFolders(folderId);
});

orchestrator(breadcrumbSelected, actionMessage => {
    const {
        breadcrumb: { id: folderId },
    } = actionMessage;

    if (folderId === PROVIDER_ROOT_FOLDER_BREADCRUMB_ID) {
        // If the selected breadcrumb is the provider breadcrumb
        // then load the root folder
        loadFolders();
    } else {
        loadFolders(folderId);
    }
});

let currentFolderBeingFetched: string = null;

/**
 * SHOULD NOT BE USED DIRECTLY. EXPORTED ONLY FOR TESTING
 */
export const loadFoldersOrchestrator = orchestrator(loadFolders, async actionMessage => {
    const { folderId } = actionMessage;
    const selectedProvider = getSelectedProvider();

    // The function will execute the provided function only if
    // it is for the folder currently being fetched. This is to avoid
    // situation where user switches to another folder before the response
    // for the first selection comes, in that case we should ignore any
    // response for the older folder being fetched.
    const runOnlyIfCurrentlyBeingFetched = (func: () => void) => {
        if (currentFolderBeingFetched === folderId) {
            func();
        }
    };

    if (selectedProvider) {
        try {
            currentFolderBeingFetched = folderId;
            loading();
            const items = await getFoldersForProvider(
                selectedProvider.type,
                selectedProvider.model.EndPointUrl,
                folderId
            );

            runOnlyIfCurrentlyBeingFetched(() => {
                updateBreadcrumbs(folderId);
                foldersLoaded(items);
            });
        } catch (error) {
            runOnlyIfCurrentlyBeingFetched(() => {
                new PerformanceDatapoint(
                    datapoints.FPFolderSelectorLoadFolderFailed.datapoint.name
                ).endWithError(DatapointStatus.ServerError, error);

                loadingFoldersFailed(FolderSelectorError.UnableToOpenFolder);
            });

            trace.warn(`Error while loading folders: ${error.toString()}`);
        } finally {
            runOnlyIfCurrentlyBeingFetched(() => {
                stoppedLoading();
            });
        }
    }
});

function updateBreadcrumbs(folderId: string | undefined | null) {
    if (!folderId || folderId === PROVIDER_ROOT_FOLDER_BREADCRUMB_ID) {
        // If there is no folder id or its a root folder id then it means that we are loading
        // root folder in that case we only need to clear all the breadcrumbs but not need
        // to add any other breadcrumb
        clearAllBreadcrumbs();
        return;
    }

    const folderName = getFolderName(folderId);

    if (folderName) {
        // Update the breadcrumbs to include the newly loaded folder
        clearAllBreadcrumbsUpTill(folderId);
        addBreadcrumb({
            key: folderId,
            id: folderId,
            name: folderName,
        });
    } else {
        // We should not get to a state where we are not able to figure
        // out the folder name. If we do then its a bug that needs further
        // investigation.
        trace.error(`Unable to find folder name for folder id: ${folderId}`);
    }
}

function getFolderName(folderId: string): string | null {
    const folderExistingBreadcrumbIfAny = getBreadcrumbForFolderId(folderId);

    if (folderExistingBreadcrumbIfAny) {
        return folderExistingBreadcrumbIfAny.name;
    }

    const folder = getFolderModelForId(folderId);
    return folder ? folder.name : null;
}
